/**
 * Le schéma d'un DIAGRAMME STATISTIQUE (`CAP-08`, `fiches/diagramme.ts`) — ce
 * qu'un auteur écrit dans une fiche, dans un énoncé, ou comme diagramme à
 * construire. Un seul schéma pour les trois : `fiche-cours.schema.ts` et
 * `manuel-exercices.ts` l'importent.
 *
 * L'auteur écrit des NOMBRES partout : une valeur absente (`null`) n'existe
 * que dans ce que le serveur sert à l'élève (`diagrammeAConstruire`).
 */
import { z } from 'zod';
const nombre = z.number().finite();
const libelle = z.string().trim().min(1).max(60);
const titre = { titre: z.string().trim().min(1).max(80).optional() };
/**
 * Une branche d'un arbre de probabilités (`CAP-12`). La probabilité est ÉCRITE,
 * exacte : « 2/5 », « 1 », « 0,25 ». Trois étapes au plus (`griefsDeDiagramme`).
 */
const probabilite = z
    .string()
    .trim()
    .regex(/^\d+(?:[.,]\d+)?(?:\/\d+)?$/, 'une probabilité s’écrit « 2/5 », « 1 » ou « 0,25 »');
const branche = z.lazy(() => z
    .object({
    issue: z.string().trim().min(1).max(12),
    p: probabilite,
    branches: z.array(branche).min(2).max(6).optional(),
})
    .strict());
export const diagrammeSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('arbre'),
        ...titre,
        etapes: z.array(z.string().trim().min(1).max(16)).min(1).max(3).optional(),
        branches: z.array(branche).min(2).max(6),
        issues: z.boolean().optional(),
        produits: z.boolean().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('venn'),
        ...titre,
        univers: z.string().trim().min(1).max(40).optional(),
        ensembles: z.array(z.string().trim().min(1).max(18)).min(2).max(3),
        regions: z.record(z.enum(['A', 'B', 'C', 'AB', 'AC', 'BC', 'ABC', 'dehors']), nombre),
    })
        .strict(),
    z
        .object({
        type: z.literal('graphe'),
        ...titre,
        sommets: z
            .array(z
            .object({
            nom: z.string().trim().min(1).max(6),
            x: z.number().min(0).max(100).optional(),
            y: z.number().min(0).max(100).optional(),
        })
            .strict())
            .min(2)
            .max(10),
        aretes: z
            .array(z
            .object({
            de: z.string().min(1),
            a: z.string().min(1),
            poids: nombre.optional(),
            oriente: z.boolean().optional(),
        })
            .strict())
            .min(1)
            .max(20),
    })
        .strict(),
    z
        .object({
        type: z.literal('bandes'),
        ...titre,
        axeX: libelle,
        axeY: libelle,
        categories: z.array(libelle).min(2).max(8),
        valeurs: z.array(nombre).min(2).max(8),
        etiquettes: z.boolean().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('ligne_brisee'),
        ...titre,
        axeX: libelle,
        axeY: libelle,
        categories: z.array(libelle).min(2).max(8),
        valeurs: z.array(nombre).min(2).max(8),
        etiquettes: z.boolean().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('circulaire'),
        ...titre,
        categories: z.array(libelle).min(2).max(8),
        valeurs: z.array(nombre).min(2).max(8),
        afficher: z.enum(['pourcentage', 'valeur', 'rien']).optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('histogramme'),
        ...titre,
        axeX: libelle,
        axeY: libelle,
        bornes: z.array(nombre).min(3).max(11),
        effectifs: z.array(nombre).min(2).max(10),
        etiquettes: z.boolean().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('quartiles'),
        ...titre,
        axe: libelle,
        min: nombre,
        q1: nombre,
        mediane: nombre,
        q3: nombre,
        max: nombre,
        graduation: z
            .object({ de: nombre, a: nombre, pas: z.number().positive() })
            .strict()
            .optional(),
        /* Plusieurs séries sur le même axe (tranche T36) : le nom de la première, et les autres. */
        nom: z.string().trim().min(1).max(14).optional(),
        autres: z
            .array(z
            .object({
            nom: z.string().trim().min(1).max(14),
            min: nombre,
            q1: nombre,
            mediane: nombre,
            q3: nombre,
            max: nombre,
        })
            .strict())
            .min(1)
            .max(3)
            .optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('nuage'),
        ...titre,
        axeX: libelle,
        axeY: libelle,
        points: z
            .array(z.object({ x: nombre, y: nombre }).strict())
            .min(2)
            .max(30),
        droite: z.object({ a: nombre, b: nombre }).strict().optional(),
    })
        .strict(),
]);
/** Le diagramme d'un énoncé ou d'une fiche : ses données, et ce qu'il montre pour qui ne le voit pas. */
export const diagrammeDecritSchema = z
    .object({ decrit: z.string().min(20).max(900), diagramme: diagrammeSchema })
    .strict();
//# sourceMappingURL=diagramme.schema.js.map